import { CaptionLabel } from '@/styles/fonts'
import { passwordStrength } from 'check-password-strength'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

enum PasswordStrengthLevelIds {
  tooWeak = 'tooWeak',
  weak = 'weak',
  ok = 'ok',
  good = 'good',
  strong = 'strong',
}

interface passwordStrengthFirstOption {
  id: number
  value: PasswordStrengthLevelIds
  minDiversity: 0
  minLength: 0
}

interface passwordStrengthOption {
  id: number
  value: PasswordStrengthLevelIds
  minDiversity: number
  minLength: number
}

const passwordStrengthOptions: [passwordStrengthFirstOption, ...Array<passwordStrengthOption>] = [
  {
    id: 0,
    value: PasswordStrengthLevelIds.tooWeak,
    minDiversity: 0,
    minLength: 0,
  },
  {
    id: 1,
    value: PasswordStrengthLevelIds.weak,
    minDiversity: 0,
    minLength: 1,
  },
  {
    id: 2,
    value: PasswordStrengthLevelIds.weak,
    minDiversity: 0,
    minLength: 8,
  },
  {
    id: 3,
    value: PasswordStrengthLevelIds.weak,
    minDiversity: 1,
    minLength: 8,
  },
  {
    id: 4,
    value: PasswordStrengthLevelIds.ok,
    minDiversity: 2,
    minLength: 8,
  },
  {
    id: 5,
    value: PasswordStrengthLevelIds.good,
    minDiversity: 3,
    minLength: 8,
  },
  {
    id: 6,
    value: PasswordStrengthLevelIds.strong,
    minDiversity: 4,
    minLength: 8,
  },
]

const PasswordStrengthText = styled(CaptionLabel)``
const PasswordStrengthLabel = styled(CaptionLabel)`
  text-align: right;
`

const PasswordStrengthHeader = styled.div`
  display: flex;
  align-items: space-between;
`

const PasswordBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  margin-bottom: 8px;
`
const PasswordStrengthDisplay = styled.div<{ $passwordStrength: PasswordStrengthLevelIds }>`
  border-radius: 8px;
  height: 8px;
  display: flex;
  padding-right: ${(props) => {
    switch (props.$passwordStrength) {
      case PasswordStrengthLevelIds.weak:
        return '90%'
      case PasswordStrengthLevelIds.ok:
        return '70%'
      case PasswordStrengthLevelIds.good:
        return '50%'
      case PasswordStrengthLevelIds.strong:
        return '0px'
      default:
        return '100%;'
    }
  }};
  align-items: center;
  align-self: stretch;
  background-color: var(--canvas-light);
`

const PasswordStrengthProgress = styled.div<{ $passwordStrength: PasswordStrengthLevelIds }>`
  height: 8px;
  flex: 1 0 0;
  border-radius: 8px;
  background: var(
    ${(props) => {
      switch (props.$passwordStrength) {
        case PasswordStrengthLevelIds.weak:
          return '--error'
        case PasswordStrengthLevelIds.ok:
          return '--warning, #F6CE55'
        case PasswordStrengthLevelIds.good: // fallsthru
        case PasswordStrengthLevelIds.strong:
          return '--success'
        default:
          return '--error'
      }
    }}
  );
`

const getPasswordStrengthLabel = (levelId: PasswordStrengthLevelIds): string => {
  switch (levelId) {
    case PasswordStrengthLevelIds.tooWeak:
      return ''
    case PasswordStrengthLevelIds.weak:
      return 'Weak'
    case PasswordStrengthLevelIds.ok:
      return 'Ok'
    case PasswordStrengthLevelIds.good:
      return 'Good'
    case PasswordStrengthLevelIds.strong:
      return 'Strong'
    default:
      return ''
  }
}

const getPasswordStrengthId = (password: string): PasswordStrengthLevelIds =>
  passwordStrength(password, passwordStrengthOptions).value

interface PasswordStrengthProps {
  password: string
}

const PasswordStrength = ({ password }: PasswordStrengthProps) => {
  const [passwordStrengthLevel, setPasswordStrengthLevel] = useState(PasswordStrengthLevelIds.tooWeak)
  useEffect(() => {
    setPasswordStrengthLevel(getPasswordStrengthId(password))
  }, [password])
  return (
    <PasswordBar>
      <PasswordStrengthHeader>
        <PasswordStrengthText fontSize="xs">Password Strength</PasswordStrengthText>
        <PasswordStrengthLabel fontSize="xs">{getPasswordStrengthLabel(passwordStrengthLevel)}</PasswordStrengthLabel>
      </PasswordStrengthHeader>
      <PasswordStrengthDisplay $passwordStrength={passwordStrengthLevel}>
        <PasswordStrengthProgress $passwordStrength={passwordStrengthLevel} />
      </PasswordStrengthDisplay>
      <PasswordStrengthText fontSize="xs">
        At least 8 characters, and contain at least 3 of the following: lowercase, uppercase letters, numbers, special
        characters
      </PasswordStrengthText>
    </PasswordBar>
  )
}

export default PasswordStrength

export { PasswordStrengthLevelIds, getPasswordStrengthId }
